import { useState } from 'react';
import clsx from 'clsx';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import {
  useAcceptUnderwriting,
  type AcceptUnderwritingResult,
} from '@/hooks/use-underwriting-events';

interface AcceptUnderwritingButtonProps {
  conversationId: string | null;
  /**
   * conversation-model-versioning Phase 5 — Accept is gated on the SAME
   * completeness mirror as Create Version (ADR-0009): all schema inputs
   * addressed + property location resolved + no orphaned inputs. Replaces
   * the prior `hasSetInputs` (≥1 input) gate, which let a user accept an
   * underwriting record that could not even be versioned (e.g. property
   * location unresolved). Computed by the caller via `computeVersionReadiness`.
   */
  versionReady: boolean;
  /**
   * Plain-language reason the control is blocked (mirrors the Create Version
   * tooltip — "Address these inputs to create a version: …"). Surfaced as the
   * disabled tooltip. No internal-flow nouns (user-facing-copy rule).
   */
  blockingReason?: string;
  /**
   * Render as a dropdown-menu row inside `<ManageVersionsButton>` instead of
   * the standalone header pill. Swaps the chrome; behavior + modal unchanged.
   */
  menuItem?: boolean;
}

type ConfidenceFlag = 'high' | 'medium' | 'low';

const CONFIDENCE_OPTIONS: ReadonlyArray<{
  value: ConfidenceFlag;
  label: string;
  description: string;
}> = [
  {
    value: 'high',
    label: 'HIGH',
    description: 'Verified terms; ready to underwrite',
  },
  {
    value: 'medium',
    label: 'MEDIUM',
    description: 'Reasonable assumptions; some open items',
  },
  {
    value: 'low',
    label: 'LOW',
    description: 'Preliminary; sensitivity to key inputs',
  },
];

const NOTES_MAX = 2000;

/**
 * Accept the current model state as an immutable underwriting record.
 *
 * Replaces the deleted `accept_underwriting` agent tool (prompt-langgraph-
 * optimization Phase 4 Session 2, 2026-05-03; Pope-resolved 2026-05-03 to
 * the VariablesPanel header placement). The agent can RECOMMEND acceptance
 * in chat, but only this user-confirmed click writes the event.
 *
 * Disabled when `conversationId` is null (Builder home, no active
 * conversation) OR when `versionReady` is false (the model is not complete
 * enough to version — same ADR-0009 gate as Create Version; you cannot
 * accept an underwriting record you cannot version). Click opens an inline
 * ConfirmModal with confidence radio + notes textarea; submit fires the
 * mutation and surfaces success or error inline.
 */
export function AcceptUnderwritingButton({
  conversationId,
  versionReady,
  blockingReason,
  menuItem = false,
}: AcceptUnderwritingButtonProps) {
  const [open, setOpen] = useState(false);
  const [confidenceFlag, setConfidenceFlag] =
    useState<ConfidenceFlag>('medium');
  const [notes, setNotes] = useState('');
  const [lastResult, setLastResult] = useState<AcceptUnderwritingResult | null>(
    null
  );

  const mutation = useAcceptUnderwriting(conversationId);
  const disabled = !conversationId || !versionReady;

  function reset() {
    setConfidenceFlag('medium');
    setNotes('');
    mutation.reset();
  }

  function close() {
    if (mutation.isPending) return; // do not close mid-write
    setOpen(false);
    reset();
  }

  function submit() {
    if (mutation.isPending) return; // guard double-clicks on the modal Confirm
    mutation.mutate(
      { confidenceFlag, notes: notes.trim() === '' ? undefined : notes.trim() },
      {
        onSuccess: (result) => {
          setLastResult(result);
          setOpen(false);
          reset();
        },
      }
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        disabled={disabled}
        data-testid="accept-underwriting-button"
        className={clsx(
          menuItem
            ? clsx(
                // Dropdown-menu row chrome (matches the other ManageVersions
                // rows): full-width, left-aligned, icon + label.
                'flex w-full items-center gap-2 px-3 py-2 text-left',
                'text-xs font-mono tracking-[0.5px] transition-colors',
                disabled
                  ? 'text-text-dim cursor-not-allowed'
                  : 'text-accent hover:bg-accent/10 cursor-pointer'
              )
            : clsx(
                'text-[10px] font-mono font-semibold tracking-[0.5px] uppercase',
                'px-2 py-0.5 rounded-[2px] border transition-colors',
                disabled
                  ? 'text-text-dim border-panel-border bg-transparent cursor-not-allowed'
                  : 'text-accent border-accent/25 bg-accent-dim hover:bg-accent/15'
              )
        )}
        title={
          !conversationId
            ? 'Open a deal to accept an underwriting.'
            : !versionReady
            ? blockingReason ?? 'Address the remaining inputs before accepting.'
            : 'Freeze current model state as an accepted underwriting record'
        }
      >
        {menuItem && <span aria-hidden="true">✓</span>}
        <span>Accept for underwriting</span>
      </button>

      {lastResult && !open && (
        <span
          data-testid="accept-underwriting-confirmation"
          className="ml-2 text-[10px] font-mono font-semibold tracking-[0.5px] uppercase text-accent"
          title={`Event ${lastResult.eventId}`}
        >
          {lastResult.versionLabel} accepted
        </span>
      )}

      <ConfirmModal
        open={open}
        onClose={close}
        onConfirm={submit}
        title="Accept Underwriting"
        confirmLabel={mutation.isPending ? 'Accepting…' : 'Accept'}
      >
        <div className="space-y-4" data-testid="accept-underwriting-modal-body">
          <div>
            <div className="text-xs font-mono font-semibold text-text-muted tracking-[0.5px] uppercase mb-2">
              Confidence
            </div>
            <div className="flex flex-col gap-2">
              {CONFIDENCE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={clsx(
                    'flex items-start gap-2 p-2 border rounded-[3px] cursor-pointer transition-colors',
                    confidenceFlag === opt.value
                      ? 'border-accent/40 bg-accent-dim'
                      : 'border-panel-border hover:bg-panel-bg'
                  )}
                >
                  <input
                    type="radio"
                    name="confidence-flag"
                    value={opt.value}
                    checked={confidenceFlag === opt.value}
                    onChange={() => setConfidenceFlag(opt.value)}
                    disabled={mutation.isPending}
                    className="mt-1 accent-accent"
                    data-testid={`confidence-radio-${opt.value}`}
                  />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-mono font-semibold text-text-bright tracking-[0.5px]">
                      {opt.label}
                    </span>
                    <span className="text-xs font-body text-text-muted">
                      {opt.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="accept-underwriting-notes"
              className="block text-xs font-mono font-semibold text-text-muted tracking-[0.5px] uppercase mb-2"
            >
              Notes (optional)
            </label>
            <textarea
              id="accept-underwriting-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
              maxLength={NOTES_MAX}
              rows={3}
              disabled={mutation.isPending}
              data-testid="accept-underwriting-notes"
              placeholder="Optional context for this acceptance — e.g. final terms agreed, open items, sensitivity caveats."
              className={clsx(
                'w-full px-2 py-1.5 text-sm font-body bg-panel-bg border border-panel-border rounded-[2px]',
                'text-text placeholder:text-text-dim resize-none',
                'focus:outline-none focus:border-accent/40'
              )}
            />
            <div className="text-[10px] font-mono text-text-dim tracking-[0.5px] mt-1 text-right">
              {notes.length} / {NOTES_MAX}
            </div>
          </div>

          {mutation.isError && (
            <div
              data-testid="accept-underwriting-error"
              className="px-3 py-2 text-xs font-body text-danger bg-danger-dim border border-danger/15 rounded-[2px]"
            >
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Failed to accept underwriting. Please try again.'}
            </div>
          )}
        </div>
      </ConfirmModal>
    </>
  );
}
